/**
 * AccountMenu — the signed-in account control at the foot of the sidebar.
 * Self-contained: fetches /me, renders nothing if not signed in. The popover
 * opens upward and holds the password form and sign-out.
 *
 * Collapsed rail shows the avatar alone.
 */

import { useEffect, useRef, useState } from "react";
import { getMe, setPassword, logout, type Me } from "../auth";
import { IconChevronDown, IconLogout } from "./icons";

/** Two initials from an email — a face for the account without an avatar service. */
function initials(email: string): string {
  const name = email.split("@")[0] || email;
  const parts = name.split(/[._-]+/).filter(Boolean);
  const s = parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
  return s.toUpperCase();
}

export function AccountMenu({ collapsed }: { collapsed?: boolean }) {
  const [me, setMe] = useState<Me | null>(null);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => { getMe().then(setMe).catch(() => setMe(null)); }, []);

  // Click-away and Escape both close it.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (!me?.authenticated) return null;
  const email = me.email ?? "account";

  return (
    <div className="account" ref={wrap}>
      <button
        className={`account-btn${open ? " active" : ""}`}
        onClick={() => setOpen(o => !o)}
        title={email}
        aria-expanded={open}
      >
        <span className="account-avatar" aria-hidden="true">{initials(email)}</span>
        <span className="account-id">
          <span className="account-email">{email}</span>
          <span className="account-role">{me.is_superadmin ? "Superadmin" : "Account"}</span>
        </span>
        <IconChevronDown size={14} className="chevron" />
      </button>

      {open && (
        <div className="account-panel" role="menu">
          <SetPassword
            hasPassword={me.has_password !== false}
            onChanged={() => getMe().then(setMe).catch(() => {})}
          />
          <button
            className="account-signout"
            onClick={async () => { await logout(); window.location.href = "/"; }}
          >
            <IconLogout size={14} />
            <span>Sign out</span>
          </button>
        </div>
      )}
      {collapsed && <span className="sr-only">{email}</span>}
    </div>
  );
}

/** Set (link-only account, no current needed) or change (has one) the password. */
function SetPassword({ hasPassword, onChanged }: { hasPassword: boolean; onChanged: () => void }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("saving"); setError("");
    try {
      await setPassword(next, hasPassword ? cur : undefined);
      setState("done"); setCur(""); setNext("");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setState("error");
    }
  };

  return (
    <form onSubmit={submit} className="account-form">
      <div className="account-form-label">{hasPassword ? "Change password" : "Set a password"}</div>
      {!hasPassword && (
        <div className="account-form-hint">
          You sign in with an email link. Add a password to also sign in with one.
        </div>
      )}
      {hasPassword && (
        <input type="password" placeholder="Current password" value={cur}
               onChange={e => setCur(e.target.value)} autoComplete="current-password" required />
      )}
      <input type="password" placeholder="New password (min 8)" value={next}
             onChange={e => setNext(e.target.value)} autoComplete="new-password" minLength={8} required />
      <button className="primary" type="submit" disabled={state === "saving"}>
        {state === "saving" ? "Saving…"
          : state === "done" ? "Saved"
          : hasPassword ? "Update password" : "Set password"}
      </button>
      {error && <div className="account-form-error">{error}</div>}
    </form>
  );
}
